import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { checkInToInspection, getInspection } from '../../api/inspection';
import { CheckCircle, Home, User, Mail, Phone, ThumbsUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { MELBOURNE_TZ } from '../../utils/melbourneTime';

const OFICheckIn = () => {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest_level: 'medium',
    budget_range: '',
    timeline: '',
    feedback: ''
  });

  useEffect(() => {
    loadInspection();
  }, [inspectionId]);

  const loadInspection = async () => {
    try {
      const response = await getInspection(inspectionId);
      setInspection(response.data);
    } catch (error) {
      console.error('Error loading inspection:', error);
      toast.error('Invalid inspection link');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await checkInToInspection(inspectionId, formData);
      setSuccess(true);
      toast.success('Thank you for checking in!');
    } catch (error) {
      console.error('Error checking in:', error);
      toast.error('Failed to check in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#55C5D0] to-[#3bb3be] flex items-center justify-center p-4">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading inspection details...</p>
        </div>
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#55C5D0] to-[#3bb3be] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-8 text-center max-w-md">
          <div className="text-red-500 mb-4">
            <Home className="w-16 h-16 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Inspection Not Found</h2>
          <p className="text-gray-600">This inspection link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#55C5D0] to-[#3bb3be] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-8 text-center max-w-md">
          <div className="text-green-500 mb-4">
            <CheckCircle className="w-16 h-16 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-6">
            You've successfully checked in to the inspection. Our agent will be in touch with you soon.
          </p>
          <div className="bg-[#55C5D0] bg-opacity-10 border border-[#55C5D0] rounded-lg p-4">
            <p className="text-sm text-gray-900">
              <strong>{inspection.property_title}</strong><br />
              {inspection.property_address}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#55C5D0] to-[#3bb3be] p-4">
      <div className="max-w-2xl mx-auto py-8">
        {/* Header */}
        <div className="bg-white rounded-t-lg p-6 border-b">
          <div className="flex items-center mb-2">
            <Home className="w-6 h-6 text-[#55C5D0] mr-2" />
            <h1 className="text-2xl font-bold text-gray-900">Open For Inspection</h1>
          </div>
          <div className="bg-[#55C5D0] bg-opacity-10 border border-[#55C5D0] rounded-lg p-4 mt-4">
            <p className="font-semibold text-gray-900">{inspection.property_title}</p>
            <p className="text-sm text-gray-700">{inspection.property_address}</p>
            <p className="text-sm text-gray-600 mt-2">
              {new Date(inspection.scheduled_date).toLocaleString('en-AU', {
                timeZone: MELBOURNE_TZ,
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-b-lg p-6">
          <p className="text-gray-600 mb-6">
            Please fill in your details to check in to this inspection.
          </p>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <User className="w-4 h-4 mr-1" />
                Full Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="John Smith"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
              />
            </div>

            {/* Email */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <Mail className="w-4 h-4 mr-1" />
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="john@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <Phone className="w-4 h-4 mr-1" />
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="0400 000 000"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
              />
            </div>

            {/* Interest Level */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <ThumbsUp className="w-4 h-4 mr-1" />
                Interest Level
              </label>
              <select
                value={formData.interest_level}
                onChange={(e) => setFormData({ ...formData, interest_level: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
              >
                <option value="low">Just looking</option>
                <option value="medium">Interested</option>
                <option value="high">Very interested</option>
                <option value="very_high">Ready to make an offer</option>
              </select>
            </div>

            {/* Budget Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Budget Range (Optional)
              </label>
              <input
                type="text"
                value={formData.budget_range}
                onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                placeholder="e.g., $800,000 - $900,000"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
              />
            </div>

            {/* Timeline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buying Timeline (Optional)
              </label>
              <select
                value={formData.timeline}
                onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
              >
                <option value="">Select timeline</option>
                <option value="Immediately">Immediately</option>
                <option value="Within 1 month">Within 1 month</option>
                <option value="Within 3 months">Within 3 months</option>
                <option value="Within 6 months">Within 6 months</option>
                <option value="Just looking">Just looking</option>
              </select>
            </div>

            {/* Feedback */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Feedback or Questions (Optional)
              </label>
              <textarea
                value={formData.feedback}
                onChange={(e) => setFormData({ ...formData, feedback: e.target.value })}
                rows="3"
                placeholder="Any thoughts or questions about the property..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
              />
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
            <p className="text-xs text-gray-600">
              By checking in, you consent to the agent contacting you about this property and similar properties that may interest you. Your information will be kept confidential.
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full mt-6 bg-[#55C5D0] text-white py-3 px-6 rounded-md font-semibold hover:bg-[#3bb3be] focus:outline-none focus:ring-2 focus:ring-[#55C5D0] disabled:opacity-50 transition-colors"
          >
            {submitting ? 'Checking In...' : 'Check In'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default OFICheckIn;

